import React, { useState, useEffect, useRef } from 'react'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    makeStyles,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import BusinessIcon from '@material-ui/icons/Business'
import SearchIcon from '@material-ui/icons/Search'
import { Company } from '../../../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: theme.spacing(2),
        minWidth: 560,
        maxWidth: 640,
        minHeight: 420,
        maxHeight: '85vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontWeight: 700,
        fontSize: '1.125rem',
    },
    content: {
        padding: theme.spacing(3),
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    section: {
        marginBottom: theme.spacing(3),
        '&:last-of-type': {
            marginBottom: 0,
        },
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    sectionIcon: {
        fontSize: 20,
        color: theme.palette.primary.main,
    },
    sectionLabel: {
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
    },
    fileList: {
        maxHeight: 100,
        overflowY: 'auto',
        padding: theme.spacing(1.5, 2),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 0),
        fontSize: '14px',
        color: theme.palette.text.primary,
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    fileIcon: {
        fontSize: 18,
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    searchField: {
        marginBottom: theme.spacing(1.5),
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.grey[50],
        },
    },
    propertyList: {
        flex: 1,
        minHeight: 280,
        maxHeight: 360,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    selectAllRow: {
        padding: theme.spacing(1.25, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        fontSize: '14px',
    },
    propertyRow: {
        padding: theme.spacing(1, 2),
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    propertyLabel: {
        fontSize: '14px',
    },
    selectionSummaryWrap: {
        marginTop: theme.spacing(1.5),
        minHeight: 20,
        display: 'flex',
        alignItems: 'center',
    },
    selectionSummary: {
        fontSize: '13px',
        color: theme.palette.text.secondary,
    },
    emptyState: {
        padding: theme.spacing(3, 2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '14px',
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1.5),
        border: `1px dashed ${theme.palette.divider}`,
    },
    actions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
        gap: theme.spacing(1.5),
    },
    cancelButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
    submitButton: {
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1, 2.5),
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none',
        },
    },
}))

export interface SelectPropertiesUploadDialogProps {
    open: boolean
    onClose: () => void
    files: File[]
    propertyList: Company[]
    title?: string
    submitLabel?: string
    children?: React.ReactNode
    onConfirm: (propertyIds: number[]) => void
    isSubmitting?: boolean
    initialSelectedIds?: number[]
    submittingLabel?: string
}

export const SelectPropertiesUploadDialog: React.FC<SelectPropertiesUploadDialogProps> = ({
    open,
    onClose,
    files,
    propertyList,
    title = 'Select properties',
    submitLabel = 'Upload',
    children,
    onConfirm,
    isSubmitting = false,
    initialSelectedIds,
    submittingLabel = 'Uploading...',
}) => {
    const classes = useStyles()
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [searchText, setSearchText] = useState('')
    const initialSelectedIdsRef = useRef(initialSelectedIds)
    initialSelectedIdsRef.current = initialSelectedIds

    useEffect(() => {
        if (open) {
            setSelectedIds(initialSelectedIdsRef.current ?? [])
            setSearchText('')
        }
    }, [open])

    const filteredProperties = React.useMemo(() => {
        if (!searchText.trim()) return propertyList
        const q = searchText.trim().toLowerCase()
        return propertyList.filter((p) => p.name.toLowerCase().includes(q))
    }, [propertyList, searchText])

    const allFilteredSelected =
        filteredProperties.length > 0 &&
        filteredProperties.every((p) => selectedIds.includes(p.id))
    const someSelected = selectedIds.length > 0

    const handleToggleAll = () => {
        if (allFilteredSelected) {
            setSelectedIds((prev) =>
                prev.filter(
                    (id) => !filteredProperties.some((p) => p.id === id),
                ),
            )
        } else {
            const toAdd = filteredProperties
                .filter((p) => !selectedIds.includes(p.id))
                .map((p) => p.id)
            setSelectedIds((prev) => [...prev, ...toAdd])
        }
    }

    const handleToggle = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        )
    }

    const handleConfirm = () => {
        if (selectedIds.length > 0) {
            onConfirm(selectedIds)
            // Parent is responsible for closing when upload completes
        }
    }

    const handleClose = () => {
        if (!isSubmitting) onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            onBackdropClick={handleClose}
            disableEscapeKeyDown={isSubmitting}
            PaperProps={{ className: classes.paper }}
        >
            <DialogTitle className={classes.title} disableTypography>
                <Typography variant="h6" component="span">
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.content}>
                {files.length > 0 && (
                    <Box className={classes.section}>
                        <Box className={classes.sectionHeader}>
                            <DescriptionIcon className={classes.sectionIcon} />
                            <Typography className={classes.sectionLabel}>
                                Files to upload ({files.length})
                            </Typography>
                        </Box>
                        <Box className={classes.fileList}>
                            {files.map((f, i) => (
                                <Box
                                    key={`${f.name}-${i}`}
                                    className={classes.fileItem}
                                >
                                    <DescriptionIcon
                                        className={classes.fileIcon}
                                    />
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {f.name}
                                    </span>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                {children}
                <Box className={classes.section}>
                    <Box className={classes.sectionHeader}>
                        <BusinessIcon className={classes.sectionIcon} />
                        <Typography className={classes.sectionLabel}>
                            Attach to properties
                        </Typography>
                    </Box>
                    {propertyList.length === 0 ? (
                        <Box className={classes.emptyState}>
                            No properties available. Add properties to your
                            organization first.
                        </Box>
                    ) : (
                        <>
                            <TextField
                                className={classes.searchField}
                                placeholder="Search properties..."
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box className={classes.propertyList}>
                                <Box className={classes.selectAllRow}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    filteredProperties.length >
                                                        0 && allFilteredSelected
                                                }
                                                indeterminate={
                                                    someSelected &&
                                                    filteredProperties.length >
                                                        0 &&
                                                    !allFilteredSelected &&
                                                    filteredProperties.some(
                                                        (p) =>
                                                            selectedIds.includes(
                                                                p.id,
                                                            ),
                                                    )
                                                }
                                                onChange={handleToggleAll}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={
                                            searchText.trim()
                                                ? `Select all (${filteredProperties.length} shown)`
                                                : 'Select all'
                                        }
                                        className={classes.propertyLabel}
                                    />
                                </Box>
                                {filteredProperties.length === 0 ? (
                                    <Box className={classes.emptyState}>
                                        No properties match &quot;{searchText}
                                        &quot;
                                    </Box>
                                ) : (
                                    filteredProperties.map((property) => (
                                        <Box
                                            key={property.id}
                                            className={classes.propertyRow}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedIds.includes(
                                                            property.id,
                                                        )}
                                                        onChange={() =>
                                                            handleToggle(
                                                                property.id,
                                                            )
                                                        }
                                                        color="primary"
                                                        size="small"
                                                    />
                                                }
                                                label={property.name}
                                                className={
                                                    classes.propertyLabel
                                                }
                                            />
                                        </Box>
                                    ))
                                )}
                            </Box>
                            <Box className={classes.selectionSummaryWrap}>
                                {selectedIds.length > 0 && (
                                    <Typography
                                        className={classes.selectionSummary}
                                    >
                                        {selectedIds.length} propert
                                        {selectedIds.length === 1
                                            ? 'y'
                                            : 'ies'}{' '}
                                        selected
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    color="default"
                    variant="outlined"
                    className={classes.cancelButton}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={
                        isSubmitting ||
                        propertyList.length === 0 ||
                        selectedIds.length === 0
                    }
                    color="primary"
                    variant="contained"
                    className={classes.submitButton}
                >
                    {isSubmitting ? submittingLabel : submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
